//------------------------------------------------------------------------
// Pie Charts
//------------------------------------------------------------------------
// NOTE: Only import the necessary modules
// https://www.chartjs.org/docs/latest/getting-started/integration.html
import {Chart, ArcElement, DoughnutController, Tooltip, PieController} from 'chart.js';
import { cursorPositioner } from '../helpers/chart-js-helpers';
import debounce from "lodash/debounce";

Chart.register(ArcElement, DoughnutController, Tooltip, PieController);
// NOTE: Treeshaking doesn’t seem to be working when importing everything
// https://github.com/chartjs/Chart.js/issues/10163
// e.g. import Chart from 'chart.js/auto';

/**
 * Custom positioner - Allows for the tooltip to follow the cursor's pointer
 */
Tooltip.positioners.cursor = cursorPositioner;

class PieChart {
  constructor(config) {
    this.el = config.el;
    this.segments = JSON.parse(this.el.getAttribute('data-segments'));
    this.canvas = this.el.querySelector('canvas');
    this.legendItems = this.el.querySelectorAll('[data-legend-item]');

    if (!this.segments) {
      console.error(`Unable to find [data-segments] data on #${this.el.id}.`);
      return false;
    }

    if (!this.canvas) {
      console.error(`Unable to initialize pie chart, no matching element for ${this.el}.`);
      return false;
    }

    this.setData();
    this.init();
  }

  init() {
    // plugin that displays the featured in the center of the donut chart
    let mql = window.matchMedia("(min-width: 700px)");

    mql.addEventListener('change', () => {
      // Returning the same values that onResize would for chart.js
      this.resizeHandler(this.chart, {width: window.innerWidth, height: window.innerHeight});
    });

    this.chart = new Chart(this.canvas, {
      type: 'pie',
      data: {
        labels: this.dataLabels,
        datasets: [{
          label: this.title,
          data: this.dataValues,
          backgroundColor: this.colors,
          borderWidth: 2,
          borderColor: '#fff',
          hoverBackgroundColor: this.colors,
          hoverBorderColor: '#fff',
        }]
      },
      options: {
        datasets: {
          pie: {
            offset: 2
          }
        },
        plugins: {
          tooltip: {
            enabled: false,
            position: 'cursor',
            // We are binding the method to have access to this.toolTipEl
            external: mql.matches ? this.externalTooltipHandler.bind(this) : false
          }
        }
      },
    });

    this.el.addEventListener('mouseout-chart', debounce(this.mouseoutChartHandler.bind(this), 10, { leading: true, trailing: false}));
    this.el.addEventListener('mouseover-chart', debounce(this.mouseoverChartHandler.bind(this), 10, { leading: true, trailing: false}));

    this.legendItemsHandler();
  }

  resizeHandler(chart, size) {
    let { width } = size;
    let tooltip = (width > 700) ? this.externalTooltipHandler.bind(this) : false;

    chart.options.plugins.tooltip.external = tooltip;
    chart.update();
  }

  mouseoutChartHandler(evt) {
    this.legendItems.forEach(item => item.classList.remove('is-inactive'));

    // Revert chart background color to its original form
    evt.detail.tooltip.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
      let colorLength = color.length;
      // Remove the original opacity from the color
      let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

      colors[index] = baseColor;
    });

    evt.detail.tooltip.chart.update();
  }

  mouseoverChartHandler(evt) {
    const { tooltip } = evt.detail;
    const selectedIndex = tooltip.dataPoints[0].dataIndex;

    this.legendItems.forEach(item => {
      let legendIndex = item.getAttribute('data-index');

      if (parseInt(legendIndex, 10) === parseInt(selectedIndex, 10)) {
        item.classList.remove('is-inactive');
      } else {
        item.classList.add('is-inactive');
      }
    });

    // Update Chart colors on hover
    evt.detail.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
      let colorLength = color.length;
      // Remove the original opacity from the color
      let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

      if (selectedIndex === index) {
        colors[index] = baseColor;
      } else {
        colors[index] = baseColor + '99';
      }
    });

    evt.detail.chart.update();
  }

  externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const tooltipEl = chart.canvas.nextElementSibling;
    // Custom event detail that is being passed through
    let detail = {
      detail: {
        el: this.el,
        tooltip,
        chart
      }
    };

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      const mouseoutChart = new CustomEvent("mouseout-chart", detail);

      this.el.dispatchEvent(mouseoutChart);
      return;
    }

    // Set Text
    if (tooltip.body) {
      let dataPoints = tooltip.dataPoints || [];
      const tooltipFragment = document.createDocumentFragment();

      dataPoints.forEach(item => {
        let titleEl = document.createElement('span');
        let titleText = document.createTextNode(item.label);
        let valueEl = document.createElement('span');
        let valueText = document.createTextNode(item.parsed + '%');

        titleEl.classList.add('ChartTooltip-title');
        valueEl.classList.add('ChartTooltip-value');

        titleEl.appendChild(titleText);
        valueEl.appendChild(valueText);

        tooltipFragment.appendChild(titleEl);
        tooltipFragment.appendChild(valueEl);
      });

      while(tooltipEl.firstChild) {
        tooltipEl.firstChild.remove();
      }

      tooltipEl.appendChild(tooltipFragment);
    }

    const { offsetLeft, offsetTop } = chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = offsetLeft + (tooltip.caretX + 10) + 'px';
    tooltipEl.style.top = offsetTop + (tooltip.caretY + 10) + 'px';

    // Emit event when we are hovering over the segment
    const mouseoverChart = new CustomEvent("mouseover-chart", detail);
    this.el.dispatchEvent(mouseoverChart);
  }

  /**
   * Retrieves data-* attributes from the Chart's container and create instance variables
   * of each in the correct format for Chart.js
   *
   * These are the data-* attributes that are being used:
   * - data-segments = the segments from the block's segments repeater field
   * - data-title = the chart's title
   * - data-type = the type of circle chart that is being used
   *
   * isntance variables that are being created:
   * - this.dataValues
   * - this.colors
   * - this.title
   * - this.type
   * @return null
   */
    setData() {
      let data = JSON.parse(this.el.getAttribute('data-segments'));
      let type = this.el.getAttribute('data-type');
      let title = this.el.getAttribute('data-title');

      this.dataLabels = data.map(segment => {
        return segment.name;
      });

      this.dataValues = data.map(segment => segment.value);
      this.colors = data.map(segment => segment.color);
      this.title = title;
      this.type = type;
    }

    legendItemsHandler() {
      this.legendItems.forEach(item => {
        item.addEventListener('mouseenter', evt => {
          let selectedIndex = parseInt(evt.target.getAttribute('data-index'), 10);

          // Deactivate all other items
          this.legendItems.forEach(legendItem => {
            if (legendItem === evt.target) {
              legendItem.classList.remove('is-inactive');
            } else {
              legendItem.classList.add('is-inactive');
            }
          });

          // Update Chart colors on hover
          this.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
            let colorLength = color.length;
            // Remove the original opacity from the color
            let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

            if (selectedIndex === index) {
              colors[index] = baseColor;
            } else {
              colors[index] = baseColor + '99';
            }
          });

          this.chart.update();
        });

        item.addEventListener('mouseleave', () => {
          this.legendItems.forEach(legendItem => {
            legendItem.classList.remove('is-inactive');
          });

          this.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
            let colorLength = color.length;
            // Remove the original opacity from the color
            let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

            colors[index] = baseColor;
          });

          this.chart.update();
        });
      });
    }
}

const init = () => {
  let pieCharts = document.querySelectorAll('[data-type="pie"]');

  if (pieCharts) {
    pieCharts.forEach(el => {
      new PieChart({ el });
    })
  }
}

// Init on initial page load
init();
